import classNames from 'classnames';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { RBACUser } from '../../../types';
import {
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
} from '../../graph/services/graphApi';
import { errorMessage, isAlreadyMember, withTransientRetry } from '../../graph/transientRetry';
import {
  selectPendingRoleAdds,
  selectPendingRoleDeletes,
  removeRoleFromPendingAdds,
  removeRoleFromPendingDeletes,
} from '../services/roleMembershipSlice';

type Props = {
  user?: RBACUser;
};

export default function SubmitChanges({ user }: Props) {
  const dispatch = useAppDispatch();
  // Open a modal to confirm all pending changes when the user clicks the button
  const [showModal, setShowModal] = useState(false);

  const pendingRoleAdds = useAppSelector(selectPendingRoleAdds);
  const pendingRoleDeletes = useAppSelector(selectPendingRoleDeletes);

  const [addGroupMember] = useAddGroupMemberMutation();
  const [removeGroupMember] = useRemoveGroupMemberMutation();

  const modalClassName = classNames('modal fixed inset-0 flex items-center justify-center', {
    'modal-open': showModal,
  });

  const showModalButtonClassName = classNames('btn btn-primary', {
    'btn-disabled': pendingRoleAdds.length === 0 && pendingRoleDeletes.length === 0,
  });

  const handleSubmit = async () => {
    if (user) {
      // withTransientRetry absorbs Graph throttling (429/503) and the concurrent-
      // modification / not-yet-replicated race — a role just added (e.g. via Copy Roles)
      // may not have replicated to the node handling the delete, so an un-retried remove
      // fails with "…does not exist / are not present" until a refresh. Same handling as
      // the Roles/ACLs submit flows and Copy Roles.
      const addPromises = pendingRoleAdds.map(async (role) => {
        try {
          await withTransientRetry(() =>
            addGroupMember({
              groupDisplayName: role.displayName,
              groupId: role.id,
              memberId: user.id,
            }).unwrap()
          );
          dispatch(removeRoleFromPendingAdds(role));
        } catch (error) {
          // Already a member is an idempotent no-op (e.g. a retried submit), not a failure.
          if (isAlreadyMember(error)) {
            dispatch(removeRoleFromPendingAdds(role));
          } else {
            toast(
              `Error adding ${user.displayName} to ${role.displayName}: ${errorMessage(error) ?? 'unknown error'}`,
              { type: 'error' }
            );
          }
        }
      });

      const deletePromises = pendingRoleDeletes.map(async (role) => {
        try {
          await withTransientRetry(() =>
            removeGroupMember({
              groupDisplayName: role.displayName,
              groupId: role.id,
              memberId: user.id,
            }).unwrap()
          );
          dispatch(removeRoleFromPendingDeletes(role));
        } catch (error) {
          toast(
            `Error removing ${user.displayName} from ${role.displayName}: ${errorMessage(error) ?? 'unknown error'}`,
            { type: 'error' }
          );
        }
      });

      //Resolve all promises, then close the modal. Spread the two arrays —
      // Promise.all([addPromises, deletePromises]) would wrap two arrays (not
      // thenables), resolving immediately and closing the modal before the
      // mutations settle.
      Promise.all([...addPromises, ...deletePromises]).then(() => {
        setShowModal(false);
      });
    }
  };

  // If the user is undefined, don't render anything
  if (!user) {
    return <div></div>;
  }

  return (
    <>
      <button
        className={showModalButtonClassName}
        onClick={() => {
          setShowModal(true);
        }}
      >
        Submit
      </button>

      <div className={modalClassName}>
        <div className="bg-base-100 rounded-lg p-5">
          <div className="font-bold text-xl mb-2">Confirm changes to {user.displayName}</div>
          <div className="pb-2">
            {pendingRoleAdds.length !== 0 && (
              <div className="py-2">
                <div className="pb-1">Roles to add:</div>
                <div className="border border-base-200 rounded-lg overflow-hidden">
                  <ul className="divide-y divide-base-200">
                    {pendingRoleAdds.map((role) => (
                      <li className="text-success-content bg-success p-2" key={role.id}>
                        {role.displayName}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {pendingRoleDeletes.length !== 0 && (
              <div className="py-2">
                <div className="pb-1">Roles to remove:</div>
                <div className="border border-base-200 rounded-lg overflow-hidden">
                  <ul className="divide-y divide-base-200">
                    {pendingRoleDeletes.map((role) => (
                      <li className="text-error-content bg-error p-2" key={role.id}>
                        {role.displayName}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div className="text-base-content mb-4">
            Are you sure you want to submit all pending changes?
          </div>
          <div className="flex justify-end">
            <button
              className="btn btn-ghost mr-2"
              onClick={() => {
                setShowModal(false);
              }}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
